import type { FC } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Text } from '@gonfalon/observability-ui/components/Text';

import { ConversationArtifactSections } from './ConversationArtifactSections';
import type { ConversationArtifactSection } from './useConversationArtifactSections';

interface Props {
  projectId: string;
  projectKey: string;
  sections: ConversationArtifactSection[];
  hasAny: boolean;
  loading: boolean;
  error: boolean;
}

/**
 * Main-pane content for the conversation view's Artifacts tab. Renders the grouped artifact
 * list centered in the same ~700px column as the chat, with loading / error / empty states
 * modeled on `VegaArtifactsPage`. Data is supplied by the tab shell (single hook instance).
 */
export const ConversationArtifactsPane: FC<Props> = ({ projectId, projectKey, sections, hasAny, loading, error }) => {
  const isInitialLoading = loading && !hasAny;
  // A query error with no rows is a transient fetch failure — show a distinct error state
  // rather than the empty state, which would wrongly imply the conversation has no artifacts.
  const hasLoadError = error && !hasAny;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="full"
      width="full"
      overflow="auto"
      p="24"
      gap="16"
      style={{ maxWidth: 'var(--vega-content-width, 700px)', margin: '0 auto' }}
    >
      {isInitialLoading ? (
        <Text>
          <Message id="vega.status.loading" />
        </Text>
      ) : hasLoadError ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1} gap="4">
          <Text>
            <Message id="vega.artifactsPane.loadError" />
          </Text>
          <Text color="moderate">
            <Message id="vega.artifactsPane.loadErrorBody" />
          </Text>
        </Box>
      ) : !hasAny ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1} gap="4">
          <Text>
            <Message id="vega.artifactsPane.emptyTitle" />
          </Text>
          <Text color="moderate">
            <Message id="vega.artifactsPane.emptyBody" />
          </Text>
        </Box>
      ) : (
        <ConversationArtifactSections projectId={projectId} projectKey={projectKey} sections={sections} />
      )}
    </Box>
  );
};
